/**
 * reportThemePresets — curated, professional/academic report theme presets.
 *
 * Each preset is a `Partial<ReportTheme>` (NEVER a full `ReportTheme`) so it
 * MERGES field-by-field onto the resolved base theme and can never clobber a
 * tenant's brand identity (companyName / logoUrl) or copy
 * (footerEmail / sidebarText / footerText / disclaimer). The actual filtering
 * of brand fields out of an applied preset lives in `resolveEffectiveTheme`.
 *
 * Tokens here mirror the user-approved `docs/design/report-themes-preview.html`.
 * All three presets are LIGHT and restrained — no dark theme (rejected as
 * gimmicky). Data/figure labels use IBM Plex Mono at the CSS level (no new
 * ReportTheme slot required).
 *
 * DOCX/PPTX serif substitute: Georgia. Sans substitute (Libre Franklin): Arial.
 * Those substitutions are wired in `reportFonts.ts` so later export phases map
 * cleanly without re-deriving the mapping here.
 *
 * Back-compat: these presets only ever reach a report through
 * `resolveEffectiveTheme` when a template explicitly carries a `themePreset`.
 * ABSENCE of `themePreset` => render with today's legacy DEFAULT_THEME, so
 * existing reports stay byte-stable on colors/fonts.
 */
import type { ReportTheme } from '../types/reportTheme';
import { wcagContrastRatio } from './resolveHeroCoverBackground';

/** The set of curated preset ids.
 *
 *  The two-column family (quarterly / review / proceedings) was REMOVED from
 *  the app surface on 2026-06-09 (see the TODO on `TWO_COLUMN_PRESET_IDS`).
 *  Their ids are KEPT here (and in THEME_PRESETS) so saved templates that
 *  reference them remain valid — they now render SINGLE-column. The default
 *  for fresh templates is `journal` (single-column editorial).
 */
export const REPORT_THEME_PRESET_IDS = [
  'journal',
  'institutional',
  'monograph',
  'quarterly',
  'review',
  'proceedings',
] as const;

/**
 * Editorial-Feature Monograph preset id (Part L — editorial-layouts-l1).
 *
 * Kept OUT of `REPORT_THEME_PRESET_IDS` on purpose: that array is the canonical
 * 6-preset matrix the visual-verification harness and every `PRESET_THEMES`
 * render-no-throw test iterate (11 × 6 = 66 baseline cells). Folding `editorial`
 * into it would silently expand the matrix + every preset table and break
 * byte-stability. The editorial preset is a NEW, flag-gated direction; it lives
 * in `THEME_PRESETS` (so `getThemePreset('editorial')` resolves it and a saved
 * template can select it) and is offered in the Studio picker ONLY when the
 * `VITE_EDITORIAL_LAYOUTS` flag is ON.
 */
export const EDITORIAL_PRESET_ID = 'editorial' as const;

/**
 * Report Themes v2 preset ids (flag-gated VITE_REPORT_THEMES_V2).
 *
 * Kept OUT of `REPORT_THEME_PRESET_IDS` for the SAME reason as `editorial`: that
 * array is the canonical 6-preset matrix the visual-verification harness + every
 * `PRESET_THEMES` render-no-throw test iterate (11 × 6 = 66 baseline cells).
 * Folding any of these into it would silently expand the matrix + break
 * byte-stability. Each is a NEW, flag-gated direction living in `THEME_PRESETS`
 * (so `getThemePreset('prospectus')` resolves it + a saved template can select
 * it), offered in the Studio picker ONLY when `VITE_REPORT_THEMES_V2` is ON.
 *   - prospectus → the investment-memorandum tombstone (ProspectusCover);
 *   - press      → the university-press frontispiece (FrontispieceCover);
 *   - signal     → the engineering drawing-sheet title block (TitleBlockCover).
 */
export const PROSPECTUS_PRESET_ID = 'prospectus' as const;
export const PRESS_PRESET_ID = 'press' as const;
export const SIGNAL_PRESET_ID = 'signal' as const;

/**
 * MEMO — the golden BTM-feasibility investment-memo voice.
 *
 * Kept OUT of `REPORT_THEME_PRESET_IDS` (the 6-preset visual-matrix matrix), for
 * the SAME byte-stability reason as `editorial` / the v2 presets: it is an
 * additive preset in `THEME_PRESETS` (so a stored template can carry it and the
 * stock-default fallback can seed it). This is the humanist-SANS memo register
 * matching the hand-built golden (`Redhead … BTM Feasibility (Draft).pdf`):
 * Helvetica/SF sans throughout, near-black ink on a white page, a single TEAL
 * accent (#10707f), and the golden semantic green/amber/red. It is the new STOCK
 * default (see `stockDefaultTemplate.ts`) so a no-custom-template report renders
 * the golden voice by default.
 */
export const MEMO_PRESET_ID = 'memo' as const;

/**
 * HOUSE — the academic-editorial WHITE house style (the new STOCK default).
 *
 * Kept OUT of `REPORT_THEME_PRESET_IDS` (the 6-preset visual-matrix driver) for
 * the SAME byte-stability reason as `memo` / `editorial` / the v2 presets: it is
 * an additive preset in `THEME_PRESETS` (so a stored template can carry it and
 * the stock-default fallback can seed it) registered ONLY in
 * `ALL_REPORT_THEME_PRESET_IDS`, so the 66-cell matrix stays byte-stable.
 *
 * This is the signed-off "academic-journal × editorial" register: a serif
 * DISPLAY face (Fraunces) over a serif reading BODY (Newsreader) on a pure-WHITE
 * page, with a grotesque-sans APPARATUS face (Hanken Grotesk) for eyebrows /
 * numerals / captions / citation markers, a single TEAL accent (#0e6f7c), and
 * the golden semantic green/amber/red. Cover-default = the editorial-masthead
 * title page. It is the new STOCK default (see `stockDefaultTemplate.ts`) so a
 * no-custom-template report renders the house style by default.
 */
export const HOUSE_PRESET_ID = 'house' as const;

/**
 * Every preset id that resolves through `THEME_PRESETS` — the 6 matrix presets
 * PLUS the flag-gated `editorial` preset AND the three flag-gated v2 presets
 * (prospectus / press / signal). `ReportThemePresetId` (and therefore
 * `template.themePreset`) is derived from THIS set so a stored template may
 * carry any of them; the 6-preset `REPORT_THEME_PRESET_IDS` stays the matrix /
 * test driver and is unchanged (byte-stable).
 */
export const ALL_REPORT_THEME_PRESET_IDS = [
  ...REPORT_THEME_PRESET_IDS,
  EDITORIAL_PRESET_ID,
  PROSPECTUS_PRESET_ID,
  PRESS_PRESET_ID,
  SIGNAL_PRESET_ID,
  MEMO_PRESET_ID,
  HOUSE_PRESET_ID,
] as const;

export type ReportThemePresetId = (typeof ALL_REPORT_THEME_PRESET_IDS)[number];

/** The user-SELECTABLE editorial preset ids — the single-column family. This
 *  is the set the Studio theme picker iterates, so it intentionally EXCLUDES
 *  the retired two-column family (quarterly / review / proceedings); those ids
 *  remain valid (in THEME_PRESETS) but are no longer offered in the picker and
 *  render single-column (see the TODO on `TWO_COLUMN_PRESET_IDS` below). */
export const SINGLE_COLUMN_PRESET_IDS = [
  'journal',
  'institutional',
  'monograph',
] as const satisfies readonly ReportThemePresetId[];

// TODO(v1.2.x+): two-column theme presets removed from picker (2026-06-09,
// William) — reimplement two-column layout correctly before re-exposing
// quarterly/review/proceedings. The two-column family was not rendering as
// expected, so it is removed from the app surface but kept REIMPLEMENTABLE:
//  - the ids stay in the type union + THEME_PRESETS map (saved templates that
//    reference them remain valid — they just render single-column);
//  - `bodyColumns` is neutered to 1 on each preset below (defence in depth);
//  - `TWO_COLUMN_PRESET_IDS` is emptied so the SINGLE column-resolution
//    decision point (`resolveBodyColumns` → checks this set) returns 1 even
//    when a preset id is present (it takes precedence over `theme.bodyColumns`);
//  - the picker's "Two column" group is removed (see ThemeTab.tsx).
// To re-expose later: restore the three ids here, restore `bodyColumns: 2` on
// the presets, and re-add the picker group — only AFTER the two-column page
// flow is fixed.
//
// MEASURED 2026-08-21 (v1.26.8 release) — what "not rendering as expected"
// concretely is, so the reimplementation starts from data rather than
// rediscovering it. These presets are NOT gated: `ci:structural-pdf` runs the
// single-column live path only; the retired family renders solely under
// `ci:structural-pdf:full`, which nothing runs automatically (deliberate — see
// the header of scripts/structural-pdf-gate.ts). Reproduce with:
//   npx tsx scripts/structural-pdf-gate.ts \
//     --types=feasibility,environmental,power-availability \
//     --presets=proceedings --full
// Result — 0/3 cells pass, three DISTINCT defects:
//   feasibility__proceedings        edge-clip p5  body exceeds printable area, bottom by  5.4pt
//   environmental__proceedings      edge-clip p8  body exceeds printable area, bottom by 10.5pt
//   power-availability__proceedings blank-tail p9 56% empty (>55% ceiling), first section "lcoeBreakdown"
// Note these renders are SINGLE-column already (TWO_COLUMN_PRESET_IDS is empty
// and the gate sets no editorial flags), so the overflow is the preset's own
// typography/geometry, NOT the two-column flow. Fixing the 2-col flow alone
// will not clear them. Reachable today only by a legacy saved template still
// referencing one of these ids — the picker has not offered them since
// 2026-06-09.
/**
 * Two-column academic preset ids. EMPTIED 2026-06-09 (see TODO above) — the
 * two-column family is removed from the app surface. Kept as an exported
 * (now-empty) constant so the type + every consumer (resolveBodyColumns,
 * TemplatePreviewPane) compile unchanged and the family can be restored in one
 * place when two-column layout is reimplemented.
 */
export const TWO_COLUMN_PRESET_IDS = [] as const satisfies readonly ReportThemePresetId[];

/** Default preset SEEDED on NEW templates. Existing templates without a
 *  `themePreset` are unaffected (they render with legacy DEFAULT_THEME).
 *  `journal` (the original single-column editorial default) — the prior
 *  two-column `quarterly` default was retired with the two-column family
 *  (see the TODO above). */
export const DEFAULT_REPORT_THEME_PRESET: ReportThemePresetId = 'journal';

/**
 * The curated presets. Each is a `Partial<ReportTheme>` — only the fields the
 * preset opinionates. Everything else falls through to the resolved base.
 *
 * CRITICAL: presets MUST NOT include `companyName` or `logoUrl` (brand
 * identity). `resolveEffectiveTheme` additionally strips those defensively.
 *
 * Per-preset DEFAULT `coverLayout` (the journal-aesthetic title pages):
 *   journal → classic; institutional → data-snapshot; monograph → minimal;
 *   quarterly / review / proceedings → masthead. `boundary-plate` is
 *   selectable on ANY theme but is never a preset default. A user's explicit
 *   `template.coverLayout` ALWAYS overrides the preset default — that
 *   precedence is enforced in `resolveEffectiveTheme` (step 4 beats step 2).
 */
export const THEME_PRESETS: Record<ReportThemePresetId, Partial<ReportTheme>> = {
  // ── JOURNAL — editorial serif, warm paper. DEFAULT for new templates. ──
  journal: {
    backgroundColor: '#fbfaf6',
    headingColor: '#1a1714',
    bodyTextColor: '#3d362e',
    primaryColor: '#155e63',
    positiveColor: '#3f6b4a',
    warningColor: '#9c6b1e',
    negativeColor: '#8a3b32',
    chartPalette: ['#155e63', '#9c6b1e', '#3f6b4a', '#8a3b32', '#3d4f7a'],
    headingFont: 'Newsreader, Georgia, serif',
    bodyFont: 'Newsreader, Georgia, serif',
    headingScale: 'editorial',
    density: 'comfortable',
    tableStyle: 'lined',
    coverLayout: 'classic',
    siteMapTreatment: 'framed-figure',
  },

  // ── INSTITUTIONAL — crisp sans, white page, exhibit framing. ──────────
  institutional: {
    backgroundColor: '#ffffff',
    headingColor: '#1b2430',
    bodyTextColor: '#46505c',
    primaryColor: '#1f3a5f',
    positiveColor: '#2f6f52',
    warningColor: '#9a6a22',
    negativeColor: '#9a3b34',
    chartPalette: ['#1f3a5f', '#9a6a22', '#2f6f52', '#9a3b34', '#5b7088'],
    headingFont: 'Libre Franklin, Arial, sans-serif',
    bodyFont: 'Libre Franklin, Arial, sans-serif',
    // Balanced, crisp technical heads (text-xl + 2px rule) — distinct from
    // the large editorial display heads of the journal/monograph family.
    headingScale: 'technical',
    density: 'comfortable',
    tableStyle: 'lined',
    // Decision-first institutional cover — oversized verdict + figure grid.
    coverLayout: 'data-snapshot',
    siteMapTreatment: 'exhibit',
  },

  // ── MONOGRAPH — Spectral serif, near-white, accent top-rule figures. ──
  monograph: {
    backgroundColor: '#fcfcfb',
    headingColor: '#14181c',
    bodyTextColor: '#444a50',
    primaryColor: '#0e7490',
    positiveColor: '#0f766e',
    warningColor: '#a16207',
    negativeColor: '#9f3a36',
    chartPalette: ['#0e7490', '#a16207', '#0f766e', '#9f3a36', '#475569'],
    headingFont: 'Spectral, Georgia, serif',
    bodyFont: 'Spectral, Georgia, serif',
    headingScale: 'editorial',
    density: 'comfortable',
    tableStyle: 'lined',
    // Monograph restraint — a single mark, one rule, vast whitespace.
    coverLayout: 'minimal',
    siteMapTreatment: 'framed-figure',
  },

  // ── QUARTERLY — the Journal family in TWO COLUMNS. NEW DEFAULT. ───────
  // Slightly smaller body type than single-column journal; comfortable lh.
  quarterly: {
    backgroundColor: '#fbfaf6',
    headingColor: '#1a1714',
    bodyTextColor: '#3a332b',
    primaryColor: '#155e63',
    positiveColor: '#3f6b4a',
    warningColor: '#9c6b1e',
    negativeColor: '#8a3b32',
    chartPalette: ['#155e63', '#9c6b1e', '#3f6b4a', '#8a3b32', '#3d4f7a'],
    headingFont: 'Newsreader, Georgia, serif',
    bodyFont: 'Newsreader, Georgia, serif',
    tableStyle: 'lined',
    // Journal voice in two columns — large editorial display heads over a
    // thin accent hairline (matches single-column Journal).
    headingScale: 'editorial',
    density: 'comfortable',
    // Journal/periodical masthead — natural for the two-column family.
    coverLayout: 'masthead',
    siteMapTreatment: 'framed-figure',
    // NEUTERED to 1 (2026-06-09) — two-column family removed from the app
    // (see TODO above). Restore to 2 when two-column layout is reimplemented.
    bodyColumns: 1,
    // Slightly smaller body (×0.875 ⇒ 14px on a 16px base, well above the
    // ~12px token floor) for the denser two-column measure.
    bodyTextScale: 0.875,
  },

  // ── REVIEW — Nature / Science voice. Sans headings, serif body. ───────
  review: {
    backgroundColor: '#fcfbf8',
    headingColor: '#14181c',
    bodyTextColor: '#3c4148',
    primaryColor: '#0e7490',
    positiveColor: '#0f766e',
    warningColor: '#a16207',
    negativeColor: '#9f3a36',
    chartPalette: ['#0e7490', '#a16207', '#0f766e', '#9f3a36', '#475569'],
    headingFont: 'Libre Franklin, Arial, sans-serif',
    bodyFont: 'Newsreader, Georgia, serif',
    tableStyle: 'lined',
    // Nature/Science two-column voice — small, uppercase, wide-tracked
    // section heads (compact) over serif body, reading like a review article.
    headingScale: 'compact',
    density: 'comfortable',
    // Journal/periodical masthead — natural for the two-column family.
    coverLayout: 'masthead',
    siteMapTreatment: 'framed-figure',
    // NEUTERED to 1 (2026-06-09) — two-column family removed from the app
    // (see TODO above). Restore to 2 when two-column layout is reimplemented.
    bodyColumns: 1,
    // Review reads at a slightly larger measure than quarterly.
    bodyTextScale: 0.9,
  },

  // ── PROCEEDINGS — IEEE/ACM density. Densest of the three. ─────────────
  proceedings: {
    backgroundColor: '#ffffff',
    headingColor: '#111418',
    bodyTextColor: '#2c2f34',
    primaryColor: '#1a3a6b',
    positiveColor: '#2f6f52',
    warningColor: '#8a6a22',
    negativeColor: '#9a3b34',
    chartPalette: ['#1a3a6b', '#8a6a22', '#2f6f52', '#9a3b34', '#5b7088'],
    headingFont: 'Libre Franklin, Arial, sans-serif',
    bodyFont: 'Newsreader, Georgia, serif',
    tableStyle: 'lined',
    // IEEE/ACM density — compact numbered-section heads (small, uppercase,
    // wide-tracked) AND the densest layout: `compact` density tightens the
    // two-column gap + inter-section spacing.
    headingScale: 'compact',
    density: 'compact',
    // Journal/periodical masthead — natural for the two-column family.
    coverLayout: 'masthead',
    siteMapTreatment: 'exhibit',
    // NEUTERED to 1 (2026-06-09) — two-column family removed from the app
    // (see TODO above). Restore to 2 when two-column layout is reimplemented.
    bodyColumns: 1,
    // Densest measure — ×0.8125 ⇒ 13px on a 16px base, still > the ~12px
    // token floor.
    bodyTextScale: 0.8125,
  },

  // ── EDITORIAL — the Editorial-Feature Monograph direction (flag-gated). ──
  // Newsreader serif display + body on warm cream paper (the journal family's
  // palette, OUR tokens — NOT an amber/vellum reskin), with IBM Plex Mono
  // labels applied at the CSS level. Single-column for now (the 2-col body is a
  // deferred follow-up). Cover-default = the new editorial-masthead title page.
  // Reachable ONLY behind VITE_EDITORIAL_LAYOUTS (kept out of the 6-preset
  // matrix), so it is byte-inert when the flag is OFF.
  [EDITORIAL_PRESET_ID]: {
    backgroundColor: '#fbfaf6',
    headingColor: '#1a1714',
    bodyTextColor: '#3d362e',
    primaryColor: '#155e63',
    positiveColor: '#3f6b4a',
    warningColor: '#9c6b1e',
    negativeColor: '#8a3b32',
    chartPalette: ['#155e63', '#9c6b1e', '#3f6b4a', '#8a3b32', '#3d4f7a'],
    headingFont: 'Newsreader, Georgia, serif',
    bodyFont: 'Newsreader, Georgia, serif',
    headingScale: 'editorial',
    density: 'comfortable',
    tableStyle: 'lined',
    // The new editorial title page — series eyebrow, dramatic serif display
    // title, standfirst abstract, KPI rail, on-cover numbered TOC.
    coverLayout: 'editorial-masthead',
    siteMapTreatment: 'framed-figure',
    // 1-col for now — the 2-col editorial body is a deferred follow-up.
    bodyColumns: 1,
  },

  // ── PROSPECTUS — the investment-memorandum direction (flag-gated v2). ──────
  // Fraunces display serif + Source Serif 4 body on warm cream paper, brass
  // accent, navy-led chart palette. Default cover = the ProspectusCover tombstone
  // title page. Reachable ONLY behind VITE_REPORT_THEMES_V2 (kept out of the
  // 6-preset matrix), so it is byte-inert when the flag is OFF. Tokens chosen to
  // pass validateThemePreset (≥5 chart colours + WCAG-AA heading/body on bg).
  [PROSPECTUS_PRESET_ID]: {
    backgroundColor: '#f6f4ee',
    headingColor: '#1a1d24',
    bodyTextColor: '#34383f',
    primaryColor: '#8a6d3b',
    positiveColor: '#3f6b4a',
    warningColor: '#9c6b1e',
    negativeColor: '#8a3b32',
    chartPalette: ['#1a3a6b', '#8a6d3b', '#3f6b4a', '#8a3b32', '#5b7088'],
    headingFont: 'Fraunces, Georgia, serif',
    bodyFont: 'Source Serif 4, Georgia, serif',
    headingScale: 'editorial',
    density: 'comfortable',
    tableStyle: 'lined',
    coverLayout: 'prospectus',
    siteMapTreatment: 'exhibit',
  },

  // ── PRESS — the university-press frontispiece direction (flag-gated v2). ───
  // EB Garamond throughout on warm paper, oxblood accent, oxblood-led palette.
  // Default cover = the FrontispieceCover centred monograph title page.
  // Reachable ONLY behind VITE_REPORT_THEMES_V2 (kept out of the 6-preset
  // matrix), byte-inert when OFF. Tokens chosen to pass validateThemePreset.
  [PRESS_PRESET_ID]: {
    backgroundColor: '#f6f3ea',
    headingColor: '#1c1a17',
    bodyTextColor: '#3a352c',
    primaryColor: '#7a2e2a',
    positiveColor: '#2f5a3f',
    warningColor: '#8a6a22',
    negativeColor: '#7a2e2a',
    chartPalette: ['#7a2e2a', '#2f5a3f', '#8a6a22', '#3a352c', '#5b5040'],
    headingFont: 'EB Garamond, Georgia, serif',
    bodyFont: 'EB Garamond, Georgia, serif',
    headingScale: 'editorial',
    density: 'comfortable',
    tableStyle: 'lined',
    coverLayout: 'frontispiece',
    siteMapTreatment: 'framed-figure',
  },

  // ── SIGNAL — the engineering drawing-sheet direction (flag-gated v2). ──────
  // Archivo grotesque headings + Hanken Grotesk body on a cool paper, blue
  // accent, blue-led palette. Technical heading scale. Default cover = the
  // TitleBlockCover drawing-sheet title block. Reachable ONLY behind
  // VITE_REPORT_THEMES_V2 (kept out of the 6-preset matrix), byte-inert when OFF.
  // Tokens chosen to pass validateThemePreset.
  [SIGNAL_PRESET_ID]: {
    backgroundColor: '#f5f7f8',
    headingColor: '#1b2026',
    bodyTextColor: '#3c434c',
    primaryColor: '#1f5fa6',
    positiveColor: '#2f6f52',
    warningColor: '#9a6a22',
    negativeColor: '#9a3b34',
    chartPalette: ['#1f5fa6', '#9a6a22', '#2f6f52', '#9a3b34', '#566370'],
    headingFont: 'Archivo, Arial, sans-serif',
    bodyFont: 'Hanken Grotesk, Arial, sans-serif',
    headingScale: 'technical',
    density: 'comfortable',
    tableStyle: 'lined',
    coverLayout: 'title-block',
    siteMapTreatment: 'exhibit',
  },

  // ── MEMO — the golden BTM-feasibility investment-memo voice (stock default). ──
  // Humanist SANS (Helvetica/SF) throughout, near-black ink on a WHITE page, a
  // single TEAL accent (#10707f) + golden semantic green/amber/red. `technical`
  // heading scale (crisp section heads + rule) is the closest to the golden's
  // compact memo heads. Matches the hand-built golden PDF. Tokens chosen to pass
  // validateThemePreset (≥5 chart colours + WCAG-AA heading/body on white).
  [MEMO_PRESET_ID]: {
    backgroundColor: '#ffffff',
    headingColor: '#1a2026',
    bodyTextColor: '#2c3138',
    primaryColor: '#10707f',
    positiveColor: '#2f6f52',
    // amber deepened (#9a6a22 → #8a5e1a) so the warning verdict label clears
    // WCAG-AA at normal size on its own pale wash, not only at large bold.
    warningColor: '#8a5e1a',
    negativeColor: '#9a3b34',
    chartPalette: ['#10707f', '#2f6f52', '#8a5e1a', '#9a3b34', '#4b5563'],
    headingFont: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    bodyFont: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    headingScale: 'technical',
    density: 'comfortable',
    tableStyle: 'lined',
    // The golden investment-memo cover (GC-W2, MemoCover). The preset floor for
    // a template that selects `memo` without an explicit cover choice.
    coverLayout: 'memo',
    siteMapTreatment: 'exhibit',
  },

  // ── HOUSE — the academic-editorial WHITE house style (stock default). ──────
  // Fraunces DISPLAY serif + Newsreader reading BODY on a pure-WHITE page, with a
  // Hanken Grotesk APPARATUS face (eyebrows / numerals / captions / citation
  // markers) carried via the new `apparatusFont` slot → `--report-apparatus-font`.
  // Single TEAL accent (#0e6f7c) + the golden semantic green/amber/red. Editorial
  // heading scale (large serif display heads) + the editorial-masthead title page.
  // Single-column. Tokens chosen to pass validateThemePreset (>=5 chart colours +
  // WCAG-AA heading/body on white). The new STOCK default (stockDefaultTemplate.ts).
  [HOUSE_PRESET_ID]: {
    backgroundColor: '#ffffff',
    headingColor: '#19191a',
    bodyTextColor: '#36352f',
    primaryColor: '#0e6f7c',
    positiveColor: '#3f6b3a',
    warningColor: '#9c5224',
    negativeColor: '#8a3b32',
    chartPalette: ['#0e6f7c', '#9c5224', '#3f6b3a', '#8a3b32', '#093f47'],
    displayFont: 'Fraunces, Georgia, serif',
    headingFont: 'Fraunces, Georgia, serif',
    bodyFont: 'Newsreader, Georgia, serif',
    apparatusFont: 'Hanken Grotesk, sans-serif',
    headingScale: 'editorial',
    density: 'comfortable',
    tableStyle: 'lined',
    coverLayout: 'editorial-masthead',
    siteMapTreatment: 'framed-figure',
    bodyColumns: 1,
  },
};

/** WCAG AA threshold for normal body text. */
const WCAG_AA_NORMAL = 4.5;

/**
 * Validate that a preset is self-consistent for the quality bar:
 *  - chartPalette has at least 5 series colours;
 *  - headingColor and bodyTextColor each clear WCAG AA against the preset
 *    backgroundColor.
 *
 * Returns the list of human-readable violations (empty when valid). Pure /
 * side-effect-free so both the build-time assertion and the unit test call it.
 */
export function validateThemePreset(
  id: string,
  preset: Partial<ReportTheme>,
): string[] {
  const errors: string[] = [];
  const palette = preset.chartPalette;
  if (!palette || palette.length < 5) {
    errors.push(
      `${id}: chartPalette must have >= 5 colours (got ${palette?.length ?? 0})`,
    );
  }
  const bg = preset.backgroundColor;
  const checkContrast = (label: string, fg: string | undefined) => {
    if (!bg || !fg) {
      errors.push(`${id}: missing ${label} or backgroundColor for contrast check`);
      return;
    }
    const ratio = wcagContrastRatio(fg, bg);
    if (ratio == null) {
      errors.push(`${id}: unparseable colour for ${label}-on-background`);
    } else if (ratio < WCAG_AA_NORMAL) {
      errors.push(
        `${id}: ${label}-on-background contrast ${ratio.toFixed(2)} < AA ${WCAG_AA_NORMAL}`,
      );
    }
  };
  checkContrast('headingColor', preset.headingColor);
  checkContrast('bodyTextColor', preset.bodyTextColor);
  return errors;
}

// ── Build-time assertion ────────────────────────────────────────────────
// Fail fast at module-load if any preset regresses the palette/contrast bar.
{
  // Validate ALL presets — the 6 matrix presets PLUS the flag-gated editorial
  // preset — so the editorial palette/contrast bar is enforced even though it
  // is intentionally excluded from the 6-preset matrix driver.
  const allErrors = ALL_REPORT_THEME_PRESET_IDS.flatMap((id) =>
    validateThemePreset(id, THEME_PRESETS[id]),
  );
  if (allErrors.length > 0) {
    throw new Error(
      `Invalid report theme preset(s):\n${allErrors.join('\n')}`,
    );
  }
}

/** Look up a preset partial by id. Returns `undefined` for unknown ids so
 *  callers can fall through to the base theme without throwing. */
export function getThemePreset(
  id: string | undefined | null,
): Partial<ReportTheme> | undefined {
  if (!id) return undefined;
  return (THEME_PRESETS as Record<string, Partial<ReportTheme>>)[id];
}

/** Type guard for a known preset id — accepts the 6 matrix presets AND the
 *  flag-gated `editorial` preset (so a stored `themePreset: 'editorial'` is
 *  recognised; gating of WHEN it is offered/applied lives at the renderer). */
export function isReportThemePresetId(
  id: string | undefined | null,
): id is ReportThemePresetId {
  return (
    id != null &&
    (ALL_REPORT_THEME_PRESET_IDS as readonly string[]).includes(id)
  );
}
